import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CalendarClock } from 'lucide-react'
import { formatFullDate, shiftEnd, today } from '../api/calendar'
import { clockTime, meridiem } from '../api/attendance'
import { fill, text } from '../api/content'
import {
  answerAsk,
  kindName,
  MY_ASKS_KEY,
  nudgeAsk,
  remindAskSubject,
  STUDIO_ASKS_KEY,
  subjectSentence,
  withdrawAsk,
  type AskRow as AskRowData,
} from '../api/asks'
import Modal from './Modal'
import PaymentLine from './PaymentLine'
import RoomPicker from './RoomPicker'
import SchedulingAssistant from './SchedulingAssistant'

type Content = Parameters<typeof text>[0]

/** What a list's label reads before Tonya has reworded it, by the kind's prompt block. */
const PROMPT_FALLBACKS: Record<string, string> = {
  'asks.answer.choose': 'Which class does it make up?',
  'asks.answer.room': 'Which room?',
}

/**
 * One request, whichever kind (D-543).
 *
 * **The row is the same for every kind, and that is the point.** What differs
 * is the sentence the kind's template makes of its subject and the word it is
 * called by; both come from the content store, so a ninth kind renders here
 * without this file changing.
 *
 * **Who is being waited on is said by the row rather than left to the reader**
 * (D-511, D-512). On an owner's row it is her, so the line says who asked; on
 * every other row it names the people, or says that nobody can answer, which
 * is the fact the studio list exists to show.
 *
 * **The answer is given here when the server says it may be** (`canAnswer`,
 * D-333) and nowhere else. A no asks for a reason first — whether it is
 * required is the kind's rule and the server enforces it; the box is offered
 * either way because a no with no words is a phone call. A kind still on its
 * old screen carries `opens` instead.
 *
 * **What a yes carries is the row's `answer`** (D-424, D-553): a list to
 * choose from, a room to pick against a time, or nothing. `counter` adds the
 * third answer — a different time back — which the server accepts only from
 * a kind that says it may be countered (D-548).
 */
export default function AskRow({ row, content }: { row: AskRowData; content: Content }) {
  const sentence = subjectSentence(row, content)
  const queryClient = useQueryClient()
  const [saying, setSaying] = useState<'no' | 'withdraw' | 'counter' | null>(null)
  const [reason, setReason] = useState('')

  /*
   * Everything the answer can have changed: both halves of this list, the
   * bell (the answerer's own row is marked read, D-547), and whatever the
   * subject shows on — a booking approved or cancelled moves the week.
   */
  const settle = () => {
    for (const key of [
      [...MY_ASKS_KEY],
      [...STUDIO_ASKS_KEY],
      ['notifications'],
      ['bookings'],
      ['calendar'],
      ['teacher-home'],
      ['portal-private-requests'],
      ['portal-dancer'],
    ]) {
      queryClient.invalidateQueries({ queryKey: key })
    }
  }

  const yes = useMutation({ mutationFn: () => answerAsk(row.id, 'yes'), onSuccess: settle })
  const no = useMutation({ mutationFn: () => answerAsk(row.id, 'no', reason), onSuccess: settle })
  const back = useMutation({ mutationFn: () => withdrawAsk(row.id, reason), onSuccess: settle })

  /*
   * The manual reminder (D-554, slice 3): the asker pokes whoever it is
   * waiting on, now. Offered when the server says it would go; a refusal
   * ("reminded 3 hours ago") is shown and the row refetched, because the
   * ledger moved under it.
   */
  const nudge = useMutation({ mutationFn: () => nudgeAsk(row.id), onSuccess: settle, onError: settle })
  /*
   * `asked` again (D-866). The success line is not decoration: `PaymentAsks` does
   * not carry `NudgedOn`, so a payment row is byte-identical after a press, and
   * without this a reminder that went out looked exactly like one that was
   * refused - which is the failure D-209 names, on the button whose whole job is
   * to email somebody.
   */
  const [askedAgain, setAskedAgain] = useState(false)

  const remind = useMutation({
    mutationFn: () => remindAskSubject(row.id),
    onSuccess: () => {
      setAskedAgain(true)
      settle()
    },
    onError: settle,
  })

  /*
   * A yes that carries something (D-424): the kind sent the choices, the
   * answer sends one back, and the server checks it against the same list.
   * "None of these" is the kind's no — an extra lesson, nothing spent (D-403)
   * — chosen on purpose from the list rather than a separate button, because
   * it settles the question for good and should not be a tap away by
   * accident. With nothing to choose, the one thing the row can do is offered
   * on its own (D-433).
   */
  const [choice, setChoice] = useState('')
  const choose = useMutation({
    mutationFn: () =>
      choice === 'none' ? answerAsk(row.id, 'no') : answerAsk(row.id, 'yes', undefined, choice),
    onSuccess: settle,
    // A refusal means the choices moved ("that absence has just been made up
    // by another lesson"), so the list is refetched before she picks again.
    onError: settle,
  })

  /*
   * A yes that books a room (D-553): the kind sent the time, the row picks a
   * room free at it, and the server books it or refuses in a sentence.
   */
  const [roomId, setRoomId] = useState('')
  const yesInRoom = useMutation({
    mutationFn: () => answerAsk(row.id, 'yes', undefined, roomId),
    onSuccess: settle,
    onError: settle,
  })

  /*
   * A different time back (D-548). Starts from the time asked for, so the
   * usual offer — same day, an hour later — is one field away; the end
   * follows the start to keep the length she was asked for.
   */
  const slot = row.answer?.room
  const [offerDate, setOfferDate] = useState(slot?.date ?? today())
  const [offerStart, setOfferStart] = useState(slot?.start.slice(0, 5) ?? '17:00')
  const [offerEnd, setOfferEnd] = useState(slot?.end.slice(0, 5) ?? '17:30')
  const [offerRoom, setOfferRoom] = useState('')

  const moveStart = (next: string) => {
    setOfferStart(next)

    // The length is kept, and the rule for that lives in one place now (D-700)
    // — the move panel on a roster asks the same question of the same fields.
    const shifted = shiftEnd(offerStart, offerEnd, next)
    if (shifted) setOfferEnd(shifted)
  }

  const counter = useMutation({
    mutationFn: () =>
      answerAsk(
        row.id,
        'countered',
        undefined,
        JSON.stringify({
          date: offerDate,
          start: `${offerStart}:00`,
          end: `${offerEnd}:00`,
          roomId: offerRoom,
        }),
      ),
    onSuccess: settle,
    onError: settle,
  })

  const busy =
    yes.isPending ||
    no.isPending ||
    back.isPending ||
    choose.isPending ||
    yesInRoom.isPending ||
    counter.isPending ||
    nudge.isPending ||
    // Without this the button stays live during its own request, and two taps a
    // second apart send the family two emails (D-866).
    remind.isPending
  const problem = (yes.error ??
    no.error ??
    back.error ??
    choose.error ??
    yesInRoom.error ??
    counter.error ??
    nudge.error ??
    // The refusals this one carries are the whole reason the control is offered
    // where the server may say no - left out, a 409 showed her nothing at all
    // and a failure was indistinguishable from a success (D-866).
    remind.error) as Error | null
  const withChoice = row.canAnswer && row.answer?.options != null
  const withRoom = row.canAnswer && slot != null
  const canCounter = row.canAnswer && row.answer?.counter === true
  const plainYes = row.canAnswer && !withChoice && !withRoom

  /*
   * <b>A yes that agrees to pay somebody is answered twice, on purpose</b>
   * (D-705). `asked`: a family agreeing to a lesson a teacher arranged sees the
   * same confirmation the request path has - *"leaves no room for a parent to
   * say i didn't know i had to pay."* The first press opens it; only the second
   * sends the yes. Keyed on the payload rather than on the kind, so any yes that
   * commits a family to paying gets it, and nothing else does.
   */
  const payment = row.answer?.payment ?? null
  const [confirmingPay, setConfirmingPay] = useState(false)

  /*
   * <b>"Nobody" means nobody who was asked, and once it is the reader's to
   * answer it has to say so</b> (D-772, found by an audit).
   *
   * The sentence was written when a row nobody could answer was a row nobody
   * could act on. The studio may now settle a makeup claim precisely because
   * the family cannot - so the bare sentence would sit directly above a live
   * control saying the opposite of it.
   *
   * <b>It never names who they are.</b> A first cut said *nobody in the
   * family*, which is true of a makeup claim and false of the two other kinds
   * that reach this branch: a level-up and a routine consent wait on
   * <i>teachers</i>, and a teacher invited and never signed in is the normal
   * state of most staff during a rollout - the case D-512 says this screen
   * exists for. The row already names them on the line above; this one only
   * has to say that the answer has fallen to the reader.
   */
  const waiting =
    row.myPart === 'owner'
      ? null
      : row.nobodyCanAnswer
        ? row.canAnswer
          // A literal, not a block: new words inside the signed-in app are
          // ours to write and ours to maintain (D-678). `asks.nobody` beside
          // it stays a block because it already is one.
          ? 'Nobody who was asked can answer this, so it is yours'
          : text(content, 'asks.nobody', 'Nobody can answer this')
        : row.waitingOn.length > 0
          ? fill(content, 'asks.waitingOn', { names: row.waitingOn.join(' or ') }, 'Waiting on {{names}}')
          : null

  const control =
    'rounded-lg border border-line-2 bg-surface px-2.5 py-1.5 text-[13px] outline-none focus:border-accent'

  return (
    <li className="rounded-xl border border-line bg-surface p-3.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-3">
          {kindName(row.kind, content)}
        </span>
        {row.late && (
          <span className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-semibold text-on-accent">
            {text(content, 'asks.late', 'Late')}
          </span>
        )}
      </div>

      <p className="mt-0.5 text-[14px] font-semibold wrap-anywhere">{sentence}</p>

      {row.happensOn && (
        <p className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5 text-[12.5px] text-ink-2">
          <CalendarClock size={14} className="translate-y-0.5" aria-hidden="true" />
          {formatFullDate(row.happensOn)}
          {row.happensAt && (
            <>
              , {clockTime(row.happensAt)}
              {meridiem(row.happensAt)}
            </>
          )}
        </p>
      )}

      {/*
        What the deadline was worked out from, on its own line rather than
        inside the subject sentence: that template is one per kind, so folding
        it in would print a literal {{anchorName}} on every task with no
        anchor. Only rendered when the fields are there (D-559).
      */}
      {row.subject.fields.anchorName && (
        <p className="mt-0.5 text-[12.5px] text-ink-3">
          {fill(
            content,
            'asks.task.anchor',
            { anchor: row.subject.fields.anchorName },
            'For {{anchor}}',
          )}
          {/*
            The date has moved since this was raised. The deadline deliberately
            did not follow it, so the row says so rather than reading as though
            the two still agree (D-227, D-559).
          */}
          {row.subject.fields.anchorMovedFrom && row.subject.fields.anchorOn && (
            <>
              {' · '}
              <span className="text-warn">
                {fill(
                  content,
                  'asks.task.anchorMoved',
                  {
                    was: formatFullDate(row.subject.fields.anchorMovedFrom),
                    now: formatFullDate(row.subject.fields.anchorOn),
                  },
                  'That date moved from {{was}} to {{now}}; this is still due when it was set',
                )}
              </span>
            </>
          )}
        </p>
      )}

      <p className="mt-0.5 text-[12.5px] text-ink-3 wrap-anywhere">
        {waiting && <>{waiting} &middot; </>}
        {fill(
          content,
          'asks.raisedBy',
          { name: row.onBehalfOf ? `${row.raisedBy} for ${row.onBehalfOf}` : row.raisedBy },
          'Asked by {{name}}',
        )}
      </p>

      {/*
        Where reminders stand, on the asker's own row (D-554): when the people
        it waits on were last reminded, and whether she left the automatic
        ones off - which the manual button below does not depend on.
      */}
      {(row.myPart === 'requester' || row.myPart === 'studio') && (row.nudgedOn || row.nudges === false) && (
        <p className="mt-0.5 text-[12px] text-ink-3">
          {row.nudgedOn &&
            fill(content, 'asks.nudge.done', { when: formatFullDate(row.nudgedOn) }, 'Reminded {{when}}')}
          {row.nudgedOn && row.nudges === false && ' · '}
          {row.nudges === false && text(content, 'asks.nudges.off', 'Automatic reminders are off for this one')}
        </p>
      )}

      {/*
        The number is the point of a chase row (D-430), and of a teacher's
        private-lesson row (D-287): one tap to the call, on the phone she is
        holding. Name and number are the same person's — the server chose
        them together.
      */}
      {row.contact && (
        <p className="mt-1 text-[13px]">
          <a
            href={`tel:${row.contact.phone}`}
            // `inline-block py-1` for the height: measured 176x17, and it is
            // the one-tap call this row exists for, on the phone she is
            // holding (D-255, D-287).
            className="inline-block py-1 font-semibold text-accent underline-offset-2 hover:underline"
            aria-label={fill(content, 'asks.chase.call', { name: row.contact.name }, 'Call {{name}}')}
          >
            {row.contact.name} &middot; {row.contact.phone}
          </a>
        </p>
      )}

      {problem && (
        <p role="alert" className="mt-2 text-[12.5px] text-crit">
          {problem.message}
        </p>
      )}

      {withChoice && row.answer!.options!.length === 0 && (
        <div className="mt-3">
          <p className="text-[12.5px] text-ink-2">
            {text(
              content,
              'asks.answer.nothingToRepair',
              'Nothing is open to make up right now: either nothing has been missed, or it has already been made up. Keeping this as an extra lesson is the only choice here; ask the studio if that looks wrong.',
            )}
          </p>
          <button
            type="button"
            onClick={() => {
              setChoice('none')
              choose.mutate()
            }}
            disabled={busy}
            className="mt-2 rounded-lg border border-line-2 px-3.5 py-2 text-[12.5px] font-semibold text-ink-2 disabled:opacity-50"
          >
            {text(content, 'asks.answer.extra', 'Keep it as an extra lesson')}
          </button>
        </div>
      )}

      {withChoice && row.answer!.options!.length > 0 && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="min-w-[16rem] flex-1">
            <span className="mb-1 block text-[12.5px] font-semibold text-ink-2">
              {text(
                content,
                row.answer!.prompt ?? 'asks.answer.choose',
                PROMPT_FALLBACKS[row.answer!.prompt ?? 'asks.answer.choose'] ?? 'Which one?',
              )}
            </span>
            <select
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
              className="w-full rounded-lg border border-line-2 bg-surface px-2.5 py-2 text-[13px] outline-none focus:border-accent"
            >
              <option value="">{text(content, 'asks.answer.pick', 'Choose one')}&hellip;</option>
              {row.answer!.options!.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                  {o.on ? ` · ${formatFullDate(o.on)}` : ''}
                </option>
              ))}
              {row.answer!.noInList && (
                <option value="none">{text(content, 'asks.answer.nothing', 'It is not making up anything')}</option>
              )}
            </select>
          </label>
          <button
            type="button"
            onClick={() => choose.mutate()}
            disabled={busy || choice === ''}
            className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-50"
          >
            {text(content, 'asks.answer.save', 'Save this choice')}
          </button>
        </div>
      )}

      {/*
        A yes that books: the room is chosen here, against the time the row is
        about, and the yes carries it (D-553). Kept out of the way while she is
        writing a no or offering another time.
      */}
      {withRoom && saying === null && (
        <div className="mt-3 flex flex-col gap-2">
          <label className="text-[12.5px] font-semibold text-ink-2" htmlFor={`room-${row.id}`}>
            {text(content, 'asks.answer.room', 'Which room?')}
          </label>
          <RoomPicker
            date={slot!.date}
            start={slot!.start.slice(0, 5)}
            end={slot!.end.slice(0, 5)}
            roomId={roomId}
            onPick={setRoomId}
            label={text(content, 'asks.answer.room', 'Which room?')}
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => yesInRoom.mutate()}
              disabled={busy || !roomId}
              className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-50"
            >
              {text(content, 'asks.answer.yesRoom', 'Yes, in this room')}
            </button>
            {canCounter && (
              <button
                type="button"
                onClick={() => setSaying('counter')}
                disabled={busy}
                className="rounded-lg border border-line-2 px-3.5 py-2 text-[12.5px] font-semibold text-ink-2 disabled:opacity-50"
              >
                {text(content, 'asks.answer.counter', 'Offer another time')}
              </button>
            )}
            <button
              type="button"
              onClick={() => setSaying('no')}
              disabled={busy}
              className="rounded-lg border border-line-2 px-3.5 py-2 text-[12.5px] font-semibold text-ink-2 hover:border-crit hover:text-crit disabled:opacity-50"
            >
              {text(content, 'asks.answer.no', 'No')}
            </button>
          </div>
        </div>
      )}

      {/*
        The one counter-offer (D-286, D-548): a day, a start, an end and a
        room she has picked. Sent as the third answer; the server ends this
        ask and raises the family's, and their yes books it.
      */}
      {canCounter && saying === 'counter' && (
        <div className="mt-3 flex flex-col gap-2">
          {/*
            The scheduling assistant, when the kind says whose weeks to lay out
            (D-553, amended): the same slot-finding the family had when they
            asked, so a teacher offering another time is not guessing at a
            grid she cannot see. The fields below follow whatever it picks and
            stay editable for a time it did not offer.
          */}
          {row.answer?.assist && (
            <SchedulingAssistant
              dancerIds={row.answer.assist.dancerIds}
              teacherStaffId={row.answer.assist.teacherStaffId}
              minutes={row.answer.assist.minutes}
              picked={{ date: offerDate, start: `${offerStart}:00` }}
              teacherLabel={row.subject.fields.teacher || 'Teacher'}
              onPick={(date, start, end) => {
                setOfferDate(date)
                setOfferStart(start.slice(0, 5))
                setOfferEnd(end.slice(0, 5))
                setOfferRoom('')
              }}
            />
          )}
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-[11.5px] font-semibold text-ink-3">
              Day
              <input
                type="date"
                value={offerDate}
                min={today()}
                onChange={(e) => setOfferDate(e.target.value)}
                className={control}
              />
            </label>
            <label className="flex flex-col gap-1 text-[11.5px] font-semibold text-ink-3">
              From
              <input
                type="time"
                value={offerStart}
                step={300}
                onChange={(e) => moveStart(e.target.value)}
                className={control}
              />
            </label>
            <label className="flex flex-col gap-1 text-[11.5px] font-semibold text-ink-3">
              To
              <input
                type="time"
                value={offerEnd}
                step={300}
                onChange={(e) => setOfferEnd(e.target.value)}
                className={control}
              />
            </label>
          </div>
          <RoomPicker
            date={offerDate}
            start={offerStart}
            end={offerEnd}
            roomId={offerRoom}
            onPick={setOfferRoom}
            label={text(content, 'asks.answer.room', 'Which room?')}
          />
          <p className="text-[11.5px] text-ink-3">
            {text(
              content,
              'asks.answer.counterHint',
              'The family will see this instead of the time they asked for. Once they answer, that is the lesson - there is no second round.',
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => counter.mutate()}
              disabled={busy || !offerRoom || offerEnd <= offerStart}
              className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-50"
            >
              {text(content, 'asks.answer.counterSend', 'Send this time instead')}
            </button>
            <button
              type="button"
              onClick={() => setSaying(null)}
              className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
            >
              {text(content, 'asks.answer.keep', 'Never mind')}
            </button>
          </div>
        </div>
      )}

      {/*
        Named for the row it opens, so sixty links do not all read "Open" to
        somebody who cannot see which row each sits under.
      */}
      {row.opens && !row.canAnswer && (
        <Link
          to={row.opens}
          aria-label={`${text(content, 'asks.open', 'Open')} ${sentence}`}
          className="mt-2 inline-block text-[13px] font-semibold text-accent"
        >
          {text(content, 'asks.open', 'Open')}
        </Link>
      )}

      {(row.canAnswer || row.canWithdraw) && (saying === 'no' || saying === 'withdraw') && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (saying === 'no' ? no : back).mutate()
              if (e.key === 'Escape') setSaying(null)
            }}
            aria-label={text(content, 'asks.answer.why', 'Why? They will see this.')}
            placeholder={text(content, 'asks.answer.why', 'Why? They will see this.')}
            className="min-w-[16rem] flex-1 rounded-lg border border-line-2 bg-surface-2 px-3 py-2 text-[13px] text-ink placeholder:text-ink-3 focus:border-accent focus:outline-none"
          />
          <button
            type="button"
            onClick={() => (saying === 'no' ? no : back).mutate()}
            disabled={busy}
            className="rounded-lg bg-crit px-3.5 py-2 text-[12.5px] font-semibold text-on-crit disabled:opacity-50"
          >
            {saying === 'no'
              ? text(content, 'asks.answer.confirmNo', 'Send the no')
              : text(content, 'asks.withdraw.confirm', 'Take it back')}
          </button>
          <button
            type="button"
            onClick={() => setSaying(null)}
            className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
          >
            {text(content, 'asks.answer.keep', 'Never mind')}
          </button>
        </div>
      )}

      {(plainYes || row.canWithdraw || row.canNudge || row.canRemindSubject) && saying === null && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {/*
            Asking the family again (D-866). Beside *Remind them* rather than
            instead of it, because they point at different people: that one pokes
            whoever the ask waits on, this one tells the family. For a money ask
            only this one is offered, since the other would email her about her
            own question.
          */}
          {row.canRemindSubject && !askedAgain && (
            <button
              type="button"
              onClick={() => remind.mutate()}
              disabled={busy}
              className="rounded-lg border border-line-2 px-3.5 py-2 text-[12.5px] font-semibold text-ink-2 disabled:opacity-50"
            >
              {remind.isPending ? 'Asking…' : 'Ask the family again'}
            </button>
          )}
          {askedAgain && (
            <span role="status" className="text-[12.5px] font-semibold text-good">
              The family has been asked again.
            </span>
          )}
          {row.canNudge && (
            <button
              type="button"
              onClick={() => nudge.mutate()}
              disabled={busy}
              className="rounded-lg border border-line-2 px-3.5 py-2 text-[12.5px] font-semibold text-ink-2 disabled:opacity-50"
            >
              {text(content, 'asks.nudge', 'Remind them')}
            </button>
          )}
          {plainYes && (
            <>
              <button
                type="button"
                onClick={() => (payment ? setConfirmingPay(true) : yes.mutate())}
                disabled={busy}
                className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-50"
              >
                {text(content, 'asks.answer.yes', 'Yes')}
              </button>
              <button
                type="button"
                onClick={() => setSaying('no')}
                disabled={busy}
                className="rounded-lg border border-line-2 px-3.5 py-2 text-[12.5px] font-semibold text-ink-2 hover:border-crit hover:text-crit disabled:opacity-50"
              >
                {text(content, 'asks.answer.no', 'No')}
              </button>
            </>
          )}
          {row.canWithdraw && (
            <button
              type="button"
              onClick={() => setSaying('withdraw')}
              disabled={busy}
              className="text-[12.5px] font-semibold text-ink-3 hover:text-ink disabled:opacity-50"
            >
              {text(content, 'asks.withdraw', 'Take it back')}
            </button>
          )}
        </div>
      )}

      {/*
        <b>The confirmation, before a yes that commits her to paying</b>
        (D-705). The request path's version is written before anyone knows the
        answer, so it hedges; here the lesson happens exactly when she agrees, so
        it says which of the three it actually is - a family on credit is told
        there is nothing to pay, not that she owes the teacher.

        <b>The refusal is inside the dialog</b> (D-697): a failed answer shown on
        the row behind a dim backdrop leaves her pressing a live button and told
        nothing.
      */}
      {confirmingPay && payment && (
        <Modal
          onDismiss={() => setConfirmingPay(false)}
          label="Before you agree"
          panelClassName="w-full max-w-[26rem]"
        >
          <div className="w-full rounded-2xl border border-line bg-surface px-5 py-5">
            <h3 className="font-display text-xl text-ink">
              {payment.route === 'FromCredit'
                ? 'This one comes out of your credit'
                : 'This one is paid to the teacher'}
            </h3>

            <PaymentLine
              route={payment.route}
              links={payment.teacherPaymentLinks}
              className="mt-2 text-[13.5px] text-ink"
            />

            {payment.route !== 'FromCredit' && (
              <p className="mt-2 text-[13px] text-ink-2">
                Private lessons are <strong>not billed through the studio</strong>.
              </p>
            )}

            <p className="mt-2 text-[12.5px] text-ink-3">
              It is not settled until you agree, and the room is held for you until then.
            </p>

            {yes.isError && (
              <p className="mt-2 text-[12.5px] font-semibold text-crit" role="alert">
                {yes.error instanceof Error ? yes.error.message : 'That could not be sent. Try again in a moment.'}
              </p>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={yes.isPending}
                onClick={() => yes.mutate(undefined, { onSuccess: () => setConfirmingPay(false) })}
                className="min-h-[36px] rounded-lg bg-accent px-3.5 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
              >
                {yes.isPending ? 'Sending…' : 'Got it, agree to the lesson'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingPay(false)}
                className="min-h-[36px] rounded-lg border border-line-2 px-3.5 text-[12.5px] font-semibold text-ink-2"
              >
                Not yet
              </button>
            </div>
          </div>
        </Modal>
      )}
    </li>
  )
}
